import { useRef } from 'react';
import { motion } from 'framer-motion';
import { Download, Share2, Sparkles, Heart, Star } from 'lucide-react';
import { toPng } from 'html-to-image';
import { FacebookIcon, WhatsappIcon } from 'react-share';
import { toast } from 'react-toastify';
import { NameMatch } from '../types';

interface MatchShareableImageProps {
  name: string;
  matches: NameMatch[];
}

export default function MatchShareableImage({ name, matches }: MatchShareableImageProps) {
  const imageRef = useRef<HTMLDivElement>(null);

  const downloadImage = async () => {
    if (!imageRef.current) return;
    
    try {
      const dataUrl = await toPng(imageRef.current, {
        quality: 1.0,
        pixelRatio: 2
      });
      
      const link = document.createElement('a');
      link.download = `name-matches-${name}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Failed to generate image:', error);
      toast.error('Failed to download image. Please try again.');
    }
  };

  const handleShare = async (platform: 'facebook' | 'whatsapp') => {
    if (!imageRef.current) return;

    try {
      const imageUrl = await toPng(imageRef.current, {
        quality: 1.0,
        pixelRatio: 2
      });

      const shareUrl = window.location.href;
      const title = `Check out who likes the name ${name}!`;
      const text = `Found perfect matches for the name ${name}!\n\nCheck out more names at:`;

      if (platform === 'facebook') {
        const fbShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&picture=${encodeURIComponent(imageUrl)}&quote=${encodeURIComponent(title + '\n' + text)}`;
        window.open(fbShareUrl, '_blank', 'width=600,height=400');
      } else {
        const waShareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(title + '\n' + text + '\n' + shareUrl)}`;
        window.open(waShareUrl, '_blank');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Failed to share. Try using the download button instead.');
    }
  };

  const icons = {
    star: <Star className="text-yellow-500" size={20} />,
    heart: <Heart className="text-red-500" size={20} />,
    sparkles: <Sparkles className="text-purple-500" size={20} />
  };

  const gradients = [
    'from-purple-100 to-pink-100',
    'from-blue-100 to-purple-100',
    'from-pink-100 to-rose-100',
    'from-indigo-100 to-blue-100'
  ];

  return (
    <div className="space-y-6">
      <div 
        ref={imageRef}
        className="relative bg-gradient-to-br from-purple-100 to-pink-100 p-8 rounded-lg"
      >
        <div className="relative z-10">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Sparkles className="text-purple-600" size={24} />
            <h3 className="text-2xl font-bold text-purple-900">
              Magical Matches for "{name}"
            </h3>
            <Sparkles className="text-purple-600" size={24} />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            {matches.map((match, index) => (
              <motion.div
                key={`${match.name}-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`bg-gradient-to-br ${gradients[index % gradients.length]} backdrop-blur-sm rounded-lg p-6 shadow-sm`}
              >
                <div className="flex items-center gap-2 mb-2">
                  {icons.sparkles}
                  <h4 className="text-lg font-medium text-purple-900">
                    {match.name}
                  </h4>
                </div>
                <p className="text-purple-600 text-sm">{match.description}</p>
                <div className="mt-2 text-xs text-purple-500">
                  Compatibility Score: {match.compatibility}%
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        
        <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none rotate-[-30deg]">
          <p className="text-4xl font-bold text-purple-900">WWW.ABH.COM.NP</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-4 justify-center">
        <button
          onClick={downloadImage}
          className="flex items-center gap-2 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Download size={20} />
          Download Image
        </button>

        <button
          onClick={() => handleShare('facebook')}
          className="flex items-center gap-2 px-6 py-3 bg-[#1877f2] text-white rounded-lg hover:bg-[#166fe5] transition-colors"
        >
          <FacebookIcon size={20} round />
          Share on Facebook
        </button>

        <button
          onClick={() => handleShare('whatsapp')}
          className="flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white rounded-lg hover:bg-[#20bd5a] transition-colors"
        >
          <WhatsappIcon size={20} round />
          Share on WhatsApp
        </button>
      </div>
    </div>
  );
}